import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import _ from "underscore";

import { SettingsPageWrapper } from "metabase/admin/components/SettingsSection";
import { StrategyForm } from "metabase/admin/performance/components/StrategyForm";
import { useCacheConfigs } from "metabase/admin/performance/hooks/useCacheConfigs";
import { useConfirmIfFormIsDirty } from "metabase/admin/performance/hooks/useConfirmIfFormIsDirty";
import { useSaveStrategy } from "metabase/admin/performance/hooks/useSaveStrategy";
import { getShortStrategyLabel } from "metabase/admin/performance/utils";
import { useSearchQuery } from "metabase/api";
import { Ellipsified } from "metabase/common/components/Ellipsified";
import { Link } from "metabase/common/components/Link";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { Sidesheet } from "metabase/common/components/Sidesheet";
import { Table } from "metabase/common/components/Table";
import type { ColumnItem } from "metabase/common/components/Table/types";
import { usePagination } from "metabase/common/hooks/use-pagination";
import { getIcon } from "metabase/lib/icon";
import {
  Button,
  Center,
  FixedSizeIcon,
  Flex,
  Repeat,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "metabase/ui";
import type { SearchResult } from "metabase-types/api";

import { type CacheableItem, getItemUrl } from "./shared";

const PAGE_SIZE = 25;

const StrategyEditorForCacheableModel = ({
  model,
  title,
  description,
}: {
  model: "dashboard" | "question";
  title: string;
  description: string;
}) => {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const { page, setPage, resetPage } = usePagination();
  const { configs, error, isLoading } = useCacheConfigs({ model: [model] });
  const searchResult = useSearchQuery({
    models: [model === "dashboard" ? "dashboard" : "card"],
    q: searchText.trim() || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    context: "search-app",
    archived: false,
  });

  const items = useMemo<CacheableItem[]>(() => {
    const strategyById = new Map(
      (configs ?? []).map((config) => [config.model_id, config.strategy]),
    );

    return (searchResult.data?.data ?? []).map((item: SearchResult) => ({
      id: Number(item.id),
      model,
      strategy: strategyById.get(Number(item.id)),
      name:
        item.name ??
        (model === "dashboard" ? t`Untitled dashboard` : t`Untitled question`),
      iconModel: model === "dashboard" ? "dashboard" : "card",
    }));
  }, [configs, model, searchResult.data?.data]);

  const total = searchResult.data?.total ?? 0;

  useEffect(() => {
    resetPage();
  }, [resetPage, searchText]);

  useEffect(() => {
    if (page > 0 && page * PAGE_SIZE >= total) {
      setPage(0);
    }
  }, [page, setPage, total]);

  useEffect(() => {
    if (targetId !== null && !items.some((item) => item.id === targetId)) {
      setTargetId(null);
    }
  }, [items, targetId]);

  const targetConfig =
    targetId !== null
      ? _.findWhere(configs ?? [], { model_id: targetId, model })
      : undefined;

  const targetName = useMemo(
    () => _.findWhere(items, { id: targetId, model })?.name,
    [items, model, targetId],
  );

  const {
    askBeforeDiscardingChanges,
    confirmationModal,
    isStrategyFormDirty,
    setIsStrategyFormDirty,
  } = useConfirmIfFormIsDirty();

  const updateTarget = useCallback(
    (id: number | null, isFormDirty: boolean) => {
      const update = () => {
        setTargetId(id);
        setIsStrategyFormDirty(false);
      };

      if (isFormDirty && targetId !== id) {
        askBeforeDiscardingChanges(update);
      } else {
        update();
      }
    },
    [askBeforeDiscardingChanges, setIsStrategyFormDirty, targetId],
  );

  const saveStrategy = useSaveStrategy(targetId, model);

  const tableColumns: ColumnItem[] = useMemo(
    () => [
      { key: "name", name: t`Name`, sortable: false },
      { key: "policy", name: t`Policy`, sortable: false },
    ],
    [],
  );

  return (
    <SettingsPageWrapper title={title} description={description}>
      <Stack gap="xl">
        {confirmationModal}
        <TextInput
          value={searchText}
          onChange={(event) => setSearchText(event.currentTarget.value)}
          placeholder={
            model === "dashboard" ? t`Search dashboards` : t`Search questions`
          }
        />
        <DelayedLoadingAndErrorWrapper
          error={error ?? searchResult.error}
          loading={
            isLoading || searchResult.isLoading || searchResult.isFetching
          }
          loader={<TableSkeleton columns={tableColumns} />}
        >
          <Table<CacheableItem>
            columns={tableColumns}
            rows={items}
            rowRenderer={(item) => (
              <CacheableItemRow
                item={item}
                currentTargetId={targetId}
                updateTarget={updateTarget}
                isFormDirty={isStrategyFormDirty}
              />
            )}
            emptyBody={<NoResultsTableRow />}
            paginationProps={
              total > PAGE_SIZE
                ? {
                    page,
                    pageSize: PAGE_SIZE,
                    total,
                    showTotal: true,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </DelayedLoadingAndErrorWrapper>
      </Stack>

      <Sidesheet
        isOpen={targetId !== null}
        onClose={() => updateTarget(null, isStrategyFormDirty)}
        title={targetName ?? t`Caching policy`}
      >
        {targetId !== null && (
          <StrategyForm
            targetId={targetId}
            targetModel={model}
            targetName=""
            setIsDirty={setIsStrategyFormDirty}
            saveStrategy={saveStrategy}
            savedStrategy={targetConfig?.strategy}
            shouldAllowInvalidation
            shouldShowName={false}
            isInSidebar
          />
        )}
      </Sidesheet>
    </SettingsPageWrapper>
  );
};

export const StrategyEditorForDashboards = () => (
  <StrategyEditorForCacheableModel
    model="dashboard"
    title={t`Dashboard caching`}
    description={t`Set caching policies for dashboards. Every dashboard is listed here, and a dashboard policy overrides any database or default policy when that dashboard runs.`}
  />
);

export const StrategyEditorForQuestions = () => (
  <StrategyEditorForCacheableModel
    model="question"
    title={t`Question caching`}
    description={t`Set caching policies for saved questions. Every saved question is listed here, and a question policy overrides any dashboard, database, or default policy.`}
  />
);

export const StrategyEditorForQuestionsAndDashboards = () => (
  <StrategyEditorForDashboards />
);

const CacheableItemRow = ({
  item,
  currentTargetId,
  updateTarget,
  isFormDirty,
}: {
  item: CacheableItem;
  currentTargetId: number | null;
  updateTarget: (id: number | null, isFormDirty: boolean) => void;
  isFormDirty: boolean;
}) => {
  const iconName = getIcon({ model: item.iconModel }).name;
  const url = getItemUrl(item.model, item);

  return (
    <tr>
      <td>
        <Flex
          align="center"
          wrap="nowrap"
          gap="sm"
          style={{ overflow: "hidden" }}
        >
          <FixedSizeIcon name={iconName} />
          {url ? (
            <Link to={url}>
              <Ellipsified style={{ fontWeight: "bold" }}>
                {item.name}
              </Ellipsified>
            </Link>
          ) : (
            <Ellipsified style={{ fontWeight: "bold" }}>
              {item.name}
            </Ellipsified>
          )}
        </Flex>
      </td>
      <td>
        <Button
          variant={currentTargetId === item.id ? "filled" : "subtle"}
          onClick={() => updateTarget(item.id, isFormDirty)}
          p={0}
        >
          {getShortStrategyLabel(item.strategy) || t`Use default`}
        </Button>
      </td>
    </tr>
  );
};

const TableSkeleton = ({ columns }: { columns: ColumnItem[] }) => (
  <Table<{ id: number }>
    columns={columns}
    rows={[{ id: 0 }, { id: 1 }, { id: 2 }]}
    rowRenderer={() => (
      <tr>
        <Repeat times={columns.length}>
          <td>
            <Skeleton h="1.5rem" w="100%" />
          </td>
        </Repeat>
      </tr>
    )}
  />
);

const NoResultsTableRow = () => (
  <tr>
    <td colSpan={2}>
      <Center py="xl">
        <Text c="text-secondary">{t`No items found.`}</Text>
      </Center>
    </td>
  </tr>
);
